"use client";

import React from "react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Loader, CircleCheck, ArrowRight, Zap, Target, ChartBar } from "lucide-react";
const inputClasses = "flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-primary/40 focus-visible:border-primary transition-all disabled:cursor-not-allowed disabled:opacity-50";
function BlueprintForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div data-section-id="143976" className="flex flex-col items-center justify-center py-12 text-center space-y-4 animate-in fade-in zoom-in duration-500">
        <div className="h-16 w-16 rounded-full bg-primary/20 flex items-center justify-center mb-4">
          <CircleCheck className="h-8 w-8 text-primary" />
        </div>
        <h3 className="text-2xl font-serif font-bold text-foreground">Application Received</h3>
        <p className="text-muted-foreground max-w-md">
          {successMessage}
        </p>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-5">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <FormField control={control} name="full_name" rules={{
        required: "Lead contact name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-sans font-medium">Lead Contact Name</FormLabel>
              <FormControl>
                <input {...field} type="text" placeholder="Alexander Sterling" className={inputClasses} />
              </FormControl>
              <FormDescription>Primary person for account management</FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Corporate email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Please enter a valid business email"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-sans font-medium">Corporate Email</FormLabel>
              <FormControl>
                <input {...field} type="email" placeholder="corporate@goldsteer.com" className={inputClasses} />
              </FormControl>
              <FormDescription>Official communications will be sent here</FormDescription>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <FormField control={control} name="company" render={({
        field
      }) => <FormItem>
              <FormLabel className="font-sans font-medium">Organization Name</FormLabel>
              <FormControl>
                <input {...field} type="text" placeholder="Global Enterprises LLC" className={inputClasses} />
              </FormControl>
              <FormDescription>The legal entity for the partnership</FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="phone" render={({
        field
      }) => <FormItem>
              <FormLabel className="font-sans font-medium">Direct Business Line</FormLabel>
              <FormControl>
                <input {...field} type="tel" placeholder="+1 415 555 0199" className={inputClasses} />
              </FormControl>
              <FormDescription>For urgent coordination and verification</FormDescription>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="goal" rules={{
      required: "Please select your monthly travel volume"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="font-sans font-medium">Estimated Monthly Spend</FormLabel>
            <Select onValueChange={field.onChange} defaultValue={field.value}>
              <FormControl>
                <SelectTrigger className="focus-visible:ring-primary/40 focus-visible:border-primary transition-all">
                  <SelectValue placeholder="Select travel volume" />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                {["Occasional ($500 - $2,000)", "Frequent ($2,000 - $5,000)", "Executive Fleet ($5,000 - $10,000)", "Enterprise ($10,000+)", "Custom Event Logistics"].map((option, i) => <SelectItem key={i} value={option} data-index={i}>
                    {option}
                  </SelectItem>)}
              </SelectContent>
            </Select>
            <FormDescription>Helps us assign the right account tier</FormDescription>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 rounded-md bg-destructive/10 border border-destructive/20 text-destructive text-sm">
          {submitError}
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full h-12 text-base font-sans font-semibold bg-primary hover:bg-primary/90 text-primary-foreground transition-all shadow-md hover:shadow-primary/20">
        {isSubmitting ? <>
            <Loader className="mr-2 h-5 w-5 animate-spin" />
            Processing Application...
          </> : <>
            Submit Partnership Application
            <ArrowRight className="ml-2 h-5 w-5" />
          </>}
      </Button>
    </Form>;
}
export default function GetYourBlueprint() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="apply-for-corporate-partnership" className="py-24 dark bg-background text-foreground relative overflow-hidden" ref={ref}>
      {}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute -top-[20%] -right-[10%] w-[50%] h-[50%] rounded-full bg-primary/10 blur-[120px]" />
        <div className="absolute -bottom-[20%] -left-[10%] w-[50%] h-[50%] rounded-full bg-chart-3/5 blur-[120px]" />
      </div>

      <div className="container mx-auto px-4 md:px-6 relative z-10">
        <div className={`max-w-6xl mx-auto transition-all duration-1000 transform ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
          <Card className="border-border/50 shadow-2xl shadow-primary/5 bg-card/80 backdrop-blur-md overflow-hidden">
            <div className="grid grid-cols-1 lg:grid-cols-5">
              
              {}
              <div className="lg:col-span-2 bg-secondary/30 p-8 md:p-12 flex flex-col justify-center border-b lg:border-b-0 lg:border-r border-border/50">
                <div className="space-y-6">
                  <div className="inline-flex items-center rounded-full border border-primary/30 bg-primary/10 px-3 py-1 text-xs font-mono font-medium text-primary tracking-wider uppercase">
                    <Zap className="mr-2 h-3.5 w-3.5" />
                    Exclusive Access
                  </div>
                  
                  <h2 className="text-3xl md:text-4xl font-serif font-semibold tracking-tight text-foreground leading-tight">
                    Elevate Your Corporate Logistics.
                  </h2>
                  
                  <p className="text-muted-foreground text-lg leading-relaxed font-sans">
                    Secure a dedicated fleet for your executive team. GoldSteer provides seamless, high-end transportation solutions tailored for the Bay Area's most demanding professionals.
                  </p>

                  <div className="space-y-4 pt-4">
                    {[{
                    icon: Target,
                    text: "Priority 24/7 Dispatch Access"
                  }, {
                    icon: ChartBar,
                    text: "Monthly Consolidated Billing"
                  }, {
                    icon: Zap,
                    text: "Dedicated Account Manager"
                  }].map((item, i) => <div key={i} className="flex items-center gap-3" data-index={i}>
                        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-background/50 shadow-sm border border-primary/20">
                          <item.icon className="h-5 w-5 text-primary" />
                        </div>
                        <span className="text-sm font-sans font-medium text-foreground">{item.text}</span>
                      </div>)}
                  </div>
                </div>
              </div>

              {}
              <div className="lg:col-span-3 p-8 md:p-12 bg-card">
                <div className="mb-8">
                  <h3 className="text-2xl font-serif font-semibold text-foreground mb-2">Corporate Application</h3>
                  <p className="text-muted-foreground font-sans">Complete the profile below to initiate your corporate partnership and unlock preferred rates.</p>
                </div>

                <FormProvider formKey="corporate_partnership_form_corporate" formId="corporate_partnership_form_corporate" sectionName="Apply for Corporate Partnership" defaultValues={{
                full_name: "",
                email: "",
                company: "",
                phone: "",
                goal: ""
              }} submitText="Submit Partnership Application" successMessage="Your application has been received. Our corporate accounts director will review your details and contact you within 24 hours to finalize your partnership terms.">
                  <BlueprintForm />
                </FormProvider>
              </div>
              
            </div>
          </Card>
        </div>
      </div>
    </section>;
}